import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Mic, Square, CheckCircle, AlertCircle, ChevronLeft, MapPin } from 'lucide-react'
import api from '../../services/api'

const RED  = '#FF3B5C'
const TEAL = '#00D4AA'
const DIM  = 'rgba(255,255,255,0.3)'

const CATEGORIES = ['incident', 'near_miss', 'hazard', 'complaint'] as const
type VoiceState = 'idle' | 'listening' | 'analysing' | 'done'

export default function MobileNewIncident() {
  const navigate = useNavigate()
  const [form, setForm] = useState({
    category: 'incident' as typeof CATEGORIES[number],
    description: '',
    date_occurred: new Date().toISOString().slice(0, 16),
    site_id: '',
    location_detail: '',
    activity_at_time: '',
    injury_type: '',
    medical_treatment: false,
    lost_time: false,
    first_aid_only: false,
    property_damage: false,
  })
  const [sites, setSites] = useState<any[]>([])
  const [voiceState, setVoiceState] = useState<VoiceState>('idle')
  const [transcript, setTranscript] = useState('')
  const [voiceError, setVoiceError] = useState('')
  const [submitError, setSubmitError] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState<{ incidentNumber: string } | null>(null)
  const recognitionRef = useRef<any>(null)
  const finalRef = useRef('')
  const mountedRef = useRef(true)

  useEffect(() => {
    api.get('/organisation/sites').then(r => setSites(Array.isArray(r.data) ? r.data : [])).catch(() => {})
    return () => {
      mountedRef.current = false
      try { recognitionRef.current?.abort() } catch {}
    }
  }, [])

  const startListening = () => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    if (!SR) { setVoiceError('Speech recognition not available — type below'); return }
    const rec = new SR()
    rec.continuous = true; rec.interimResults = true; rec.lang = 'en-AU'
    recognitionRef.current = rec; finalRef.current = ''
    setTranscript(''); setVoiceError('')

    rec.onresult = (e: any) => {
      let interim = ''
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalRef.current += e.results[i][0].transcript + ' '
        else interim += e.results[i][0].transcript
      }
      setTranscript(finalRef.current + interim)
    }
    rec.onerror = (e: any) => {
      if (!mountedRef.current || e.error === 'aborted') return
      setVoiceState('idle')
      setVoiceError(e.error === 'not-allowed' ? 'Microphone denied' : `Error: ${e.error}`)
    }
    rec.onend = async () => {
      if (!mountedRef.current) return
      const text = finalRef.current.trim()
      if (!text) { setVoiceState('idle'); setVoiceError('Nothing captured — try again'); return }
      setVoiceState('analysing')
      try {
        const { data } = await api.post('/incidents/voice-quick', { transcript: text })
        if (!mountedRef.current) return
        if (data.extracted) {
          setForm(f => ({
            ...f,
            description: data.extracted.description || f.description,
            location_detail: data.extracted.location || f.location_detail,
            activity_at_time: data.extracted.activity || f.activity_at_time,
          }))
        }
        setVoiceState('done')
      } catch {
        if (!mountedRef.current) return
        setVoiceState('idle'); setVoiceError('Analysis failed — fill form below')
      }
    }
    rec.start(); setVoiceState('listening')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(''); setLoading(true)
    try {
      const payload = { ...form, site_id: form.site_id || undefined }
      const { data } = await api.post('/incidents', payload)
      if (data?._queued) {
        // Offline — queued
        setSubmitted({ incidentNumber: 'QUEUED' })
      } else {
        setSubmitted({ incidentNumber: data.incident_number || 'Submitted' })
      }
    } catch (err: any) {
      const details = err.response?.data?.details
      const firstDetail = details?.[0]?.message
      setSubmitError(firstDetail || err.response?.data?.error || 'Failed to submit. Check your connection and try again.')
      setLoading(false)
    }
  }

  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }))

  const labelStyle = { fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' as const, letterSpacing: '0.08em', display: 'block', marginBottom: 6 }

  // Success confirmation screen
  if (submitted) {
    const isQueued = submitted.incidentNumber === 'QUEUED'
    return (
      <div style={{ background: 'var(--bg)', minHeight: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, gap: 20, textAlign: 'center' }}>
        <div style={{ width: 80, height: 80, borderRadius: '50%', background: isQueued ? `${RED}20` : `${TEAL}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {isQueued
            ? <AlertCircle size={40} color={RED} />
            : <CheckCircle size={40} color={TEAL} />}
        </div>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: '#FFFFFF', margin: '0 0 8px' }}>
            {isQueued ? 'Saved Offline' : 'Incident Reported'}
          </h2>
          {isQueued ? (
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', margin: 0, lineHeight: 1.5 }}>
              No connection detected. Your report has been saved and will sync automatically when you're back online.
            </p>
          ) : (
            <>
              <div style={{ fontSize: 28, fontWeight: 900, color: TEAL, letterSpacing: '-0.02em', margin: '8px 0' }}>
                {submitted.incidentNumber}
              </div>
              <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', margin: 0 }}>
                Logged and queued for AI analysis
              </p>
            </>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', maxWidth: 300 }}>
          <button
            onClick={() => navigate('/m/incidents/new')}
            style={{ padding: '14px', borderRadius: 16, fontSize: 14, fontWeight: 700, color: '#FFFFFF', background: RED, border: 'none', cursor: 'pointer' }}
          >
            Report Another
          </button>
          <button
            onClick={() => navigate('/m/incidents')}
            style={{ padding: '14px', borderRadius: 16, fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.6)', background: 'rgba(255,255,255,0.07)', border: 'none', cursor: 'pointer' }}
          >
            View All Incidents
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100%' }}>
      {/* Header */}
      <div
        className="sticky top-0 z-10"
        style={{ background: '#0F1823', borderBottom: '1px solid rgba(255,255,255,0.07)', padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12 }}
      >
        <button
          onClick={() => navigate('/m/incidents')}
          style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
        >
          <ChevronLeft size={18} color="rgba(255,255,255,0.7)" />
        </button>
        <h1 style={{ fontSize: 17, fontWeight: 700, color: '#FFFFFF', margin: 0 }}>Report Incident</h1>
      </div>

      <form onSubmit={handleSubmit} style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 32 }}>

        {/* Voice capture */}
        <div style={{
          borderRadius: 18,
          padding: 16,
          background: voiceState === 'done' ? `${TEAL}15` : 'var(--white)',
          border: voiceState === 'done' ? `1px solid ${TEAL}40` : '1px solid rgba(255,255,255,0.06)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>Voice Description</p>
            {voiceState === 'done' && (
              <button type="button" onClick={() => { setVoiceState('idle'); setTranscript(''); finalRef.current = '' }}
                style={{ background: 'none', border: 'none', color: DIM, cursor: 'pointer', padding: 4 }}>
                ✕
              </button>
            )}
          </div>

          {voiceState === 'idle' && (
            <button type="button" onClick={startListening}
              style={{ width: '100%', padding: '14px', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: '#FFFFFF', fontWeight: 600, fontSize: 14, background: `${RED}25`, border: `1px solid ${RED}35`, cursor: 'pointer' }}>
              <Mic size={20} color={RED} /> Tap to speak
            </button>
          )}
          {voiceState === 'listening' && (
            <button type="button" onClick={() => { try { recognitionRef.current?.stop() } catch {} }}
              style={{ width: '100%', padding: '14px', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: '#FFFFFF', fontWeight: 600, fontSize: 14, background: RED, border: 'none', cursor: 'pointer' }}>
              <Square size={16} fill="white" /> Tap to stop
            </button>
          )}
          {voiceState === 'analysing' && (
            <div style={{ width: '100%', padding: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 13, color: DIM }}>
              <span style={{ width: 16, height: 16, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
              Analysing…
            </div>
          )}
          {voiceState === 'done' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: TEAL }}>
              <CheckCircle size={17} /> Form filled from voice — review below
            </div>
          )}
          {(transcript && voiceState !== 'done') && (
            <p style={{ fontSize: 12, padding: '10px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.07)', marginTop: 10, marginBottom: 0 }}>
              {transcript}
              {voiceState === 'listening' && <span style={{ animation: 'pulse 1s infinite' }}>▌</span>}
            </p>
          )}
          {voiceError && (
            <p style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: RED, marginTop: 8, marginBottom: 0 }}>
              <AlertCircle size={13} /> {voiceError}
            </p>
          )}
        </div>

        {/* Category & Date */}
        <div style={{ background: 'var(--white)', borderRadius: 18, padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Category *</label>
              <select value={form.category} onChange={e => set('category', e.target.value)} style={{ fontSize: 16 }}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Date & Time *</label>
              <input type="datetime-local" value={form.date_occurred} onChange={e => set('date_occurred', e.target.value)} required style={{ fontSize: 16 }} />
            </div>
          </div>
        </div>

        {/* Site */}
        {sites.length > 0 && (
          <div style={{ background: 'var(--white)', borderRadius: 18, padding: 16 }}>
            <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 5 }}>
              <MapPin size={11} style={{ opacity: 0.5 }} /> Site
            </label>
            <select value={form.site_id} onChange={e => set('site_id', e.target.value)} style={{ fontSize: 16 }}>
              <option value="">— Select site (optional) —</option>
              {sites.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        )}

        {/* Description + fields */}
        <div style={{ background: 'var(--white)', borderRadius: 18, padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle}>Description *</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} required rows={4}
              placeholder="What happened?" style={{ fontSize: 16 }} />
          </div>
          <div>
            <label style={labelStyle}>Location</label>
            <input value={form.location_detail} onChange={e => set('location_detail', e.target.value)}
              placeholder="e.g. Warehouse Zone B" style={{ fontSize: 16 }} />
          </div>
          <div>
            <label style={labelStyle}>Activity at Time</label>
            <input value={form.activity_at_time} onChange={e => set('activity_at_time', e.target.value)}
              placeholder="e.g. Manual handling" style={{ fontSize: 16 }} />
          </div>
          <div>
            <label style={labelStyle}>Injury Type</label>
            <input value={form.injury_type} onChange={e => set('injury_type', e.target.value)}
              placeholder="e.g. Laceration, Sprain" style={{ fontSize: 16 }} />
          </div>
        </div>

        {/* Outcome checkboxes */}
        <div style={{ background: 'var(--white)', borderRadius: 18, padding: 16 }}>
          <p style={{ ...labelStyle, marginBottom: 14 }}>Outcome</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              ['medical_treatment', 'Medical Treatment Required'],
              ['lost_time', 'Lost Time Injury'],
              ['first_aid_only', 'First Aid Only'],
              ['property_damage', 'Property Damage'],
            ].map(([key, label]) => (
              <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
                <input type="checkbox" checked={(form as any)[key]} onChange={e => set(key, e.target.checked)}
                  style={{ width: 20, height: 20, accentColor: RED }} />
                <span style={{ fontSize: 14, color: '#FFFFFF' }}>{label}</span>
              </label>
            ))}
          </div>
        </div>

        {submitError && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderRadius: 14, background: `${RED}18`, color: RED, fontSize: 13 }}>
            <AlertCircle size={15} /> {submitError}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%',
            padding: '15px',
            borderRadius: 16,
            fontSize: 15,
            fontWeight: 700,
            color: '#FFFFFF',
            background: loading ? 'rgba(255,255,255,0.15)' : RED,
            border: 'none',
            cursor: loading ? 'default' : 'pointer',
            boxShadow: loading ? 'none' : `0 0 20px ${RED}40`,
          }}
          className="active:scale-[0.98] transition-transform"
        >
          {loading ? 'Submitting…' : 'Submit Incident'}
        </button>
      </form>
    </div>
  )
}
